import { applySubscriberFunction } from '../content/market';
import type { SinkEntity } from './entities';
import type { SimEvent } from './events';
import { peekPort, tryTakeFromPort } from './port';
import type { SimState } from './state';
import { errorSubscription, thrownMessage } from './subscription';

/**
 * A Subscriber with nothing installed in `next` consumes nothing, so packets back up at the port —
 * the same backpressure every other full port already shows. Once a method *is* installed, every
 * arriving packet is handed to it: one it cannot accept throws, and an unhandled throw in `next`
 * terminates the whole subscription rather than quietly stalling the line.
 */
export function stepSink(state: SimState, sink: SinkEntity): SimEvent[] {
  const events: SimEvent[] = [];
  const installed = state.subscriber?.functions.next ?? [];
  if (installed.length === 0) return events;

  while (true) {
    const packetId = peekPort(sink.input);
    if (packetId === undefined) break;
    const packet = state.packets[packetId];

    tryTakeFromPort(sink.input);
    if (!packet) continue;

    try {
      applySubscriberFunction(state, sink, packet, installed);
    } catch (error) {
      events.push(
        ...errorSubscription(state, {
          packetId,
          material: packet.material,
          message: thrownMessage(error),
        }),
      );
      break;
    }

    events.push({ type: 'packetSold', packetId, material: packet.material });
    delete state.packets[packetId];
    events.push({ type: 'packetDespawned', packetId });
  }

  return events;
}
